import React from "react";
import PropTypes from "prop-types";

import Form from "lib/common/descriptorcontrols/Form/form";
import Dispatcher from "Dispatcher";
import SearchFormActions from "data/SearchFormActions";
import SearchFormStore from "data/SearchFormStore";
import useFluxStore from "@armus/use-flux-store";

export class InnerSearchForm extends React.Component {
    constructor(props) {
        super(props);
        this.advancedInputRef = React.createRef();

        this.advancedToggle = this.advancedToggle.bind(this);

        this.onClear = this.onClear.bind(this);
        this.onSubmit = this.onSubmit.bind(this);

        this.state = {
            showAdvanced: props.alwaysShowAdvanced || props.showAdvanced || false,
            height: props.alwaysShowAdvanced || props.showAdvanced ? 0 : 34
        };
    }

    componentDidUpdate() {
        const height = this.advancedInputRef.current.scrollHeight;
        if (this.state.showAdvanced && this.state.height !== 0 && this.state.height !== this.advancedInputRef.current.scrollHeight) {
            this.setState({height: height});
        }
    }

    advancedToggle(e) {
        const showAdvanced = !this.state.showAdvanced;
        e.preventDefault();
        const height = showAdvanced ? (this.advancedInputRef.current.scrollHeight) : 34;
        this.setState({showAdvanced: showAdvanced, height: height });
        const {
            onHeightChange,
        } = this.props;
        onHeightChange && onHeightChange();
    }

    onSubmit(e) {
        const {onSubmit} = this.props;
        e.preventDefault();
        onSubmit && onSubmit(e);
    }

    onClear(e) {
        const {onClearFields} = this.props;
        e.preventDefault();
        onClearFields && onClearFields();
    }

    render() {
        const {
            searchFields,
            fieldPropsResolver,
            alwaysShowAdvanced,
            onToggleRelativeDates,
            supportsRelativeDates,
            hasDateFields,
            useRelativeDates,
        } = this.props;
        const {showAdvanced} = this.state;
        // first field will always be the default basic
        const formDescriptor = convertFieldToDescriptor(showAdvanced ? searchFields : searchFields.slice(0, 1));
        return (
            <form className="form-search" action="#unbound" onSubmit={this.onSubmit}>
                <fieldset>
                    <div className="advanced-search-inputs row">
                        <div className="search-inputs col-md-12" ref={this.advancedInputRef} >
                            <Form
                                descriptor={formDescriptor}
                                propsResolver={fieldPropsResolver}
                                controlOnly={true}
                                placeholder={true}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4">
                            <a href="#" className="clear" onClick={this.onClear}>
                                <span className="glyphicon glyphicon-remove" />{" "}
                                clear search
                            </a>
                        </div>
                        <div className="col-md-4">
                            {hasDateFields && supportsRelativeDates && <a href="#" className="togglerelativedates" onClick={(e) => onToggleRelativeDates(e, useRelativeDates)}>
                                <span className="glyphicon glyphicon-calendar" />{" "}
                                {!useRelativeDates ? "use last n days" : "use date range"}
                            </a>}
                        </div>
                        <div className="col-md-4">
                            {(searchFields.length > 1 && !alwaysShowAdvanced ) &&
                            <a href={"#"} className="advanced-search-toggle pull-right" onClick={this.advancedToggle}>
                                <span className={`glyphicon glyphicon-${showAdvanced ? "minus" : "plus"}`} />{" "}
                                {showAdvanced ? "hide search options" : "show search options"}
                            </a>
                            }
                        </div>
                    </div>
                </fieldset>
                <button type="submit" className="hidden">SEARCH</button>
            </form>
        );
    }
}

InnerSearchForm.propTypes = {
    fluxContext: PropTypes.string.isRequired,
    showAdvanced: PropTypes.bool,
    alwaysShowAdvanced: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
    searchFields: PropTypes.array.isRequired,
    supportsRelativeDates: PropTypes.bool,
    hasDateFields: PropTypes.bool,
    useRelativeDates: PropTypes.bool,
    fieldPropsResolver: PropTypes.func.isRequired,
    onClearFields: PropTypes.func.isRequired,
    onToggleRelativeDates: PropTypes.func.isRequired,
    onHeightChange: PropTypes.func,
};

const SearchForm = props => {
    const { fluxContext } = props;
    const state = useFluxStore(() => ({
        searchFields: SearchFormStore.getFields(fluxContext),
        hasDateFields: SearchFormStore.hasDateFields(fluxContext),
        useRelativeDates: SearchFormStore.getUseRelativeDates(fluxContext),
        onClearFields: () => {
            Dispatcher.contextDispatch(fluxContext, {
                type: SearchFormActions.CLEAR_FIELDS,
            });
        },
        onToggleRelativeDates: (e, useRelativeDates) => {
            e.preventDefault();
            Dispatcher.contextDispatch(fluxContext, {
                type: SearchFormActions.SET_USE_RELATIVE_DATES,
                useRelativeDates: !useRelativeDates,
            });
        },
        fieldPropsResolver: (field) => ({
            // build the props for each control.
            descriptor: convertFieldToDescriptor(field),
            onCommitChange: (d, value) => {
                Dispatcher.contextDispatch(fluxContext, {
                    type: SearchFormActions.SET_FIELD,
                    path: d.path,
                    value: value !== null ? value : null,
                });
            },
            value: field.value !== undefined ? field.value : null,
            enabled: true,
        })
    }), [
        SearchFormStore,
    ], [fluxContext]);
    return <InnerSearchForm {...props} {...state} />;
};

SearchForm.propTypes = {
    fluxContext: PropTypes.string.isRequired,
};

export default SearchForm;

/**
 * Converts a SearchFormStore field into a descriptor object.
 *
 * @param {array, object} field
 * @return {object}
 */
export const convertFieldToDescriptor = (field) => {
    if(Array.isArray(field)) {
        // initial Form descriptor
        return {
            key: "",
            id: "",
            path: "",
            label: "",
            type: "Form",
            control: "form",
            disableBehaviour: "disable",
            childItems: field,
        };
    }

    const descriptor = {
        config: {},
        key: field.key,
        id: field.id || field.key,
        path: field.path,
        label: field.name,
        disableBehaviour: "disable"
    };
    switch (field.type) {
        case "string":
            if (field.hasExpectedValues) {
                descriptor.control = "dropdown";
                descriptor.config = {
                    options: ["", ...field.expectedValues],
                };
            } else {
                descriptor.control = "text";
            }
            descriptor.type = "StringField";
            break;
        case "checkbox":
            descriptor.control = "checkbox";
            descriptor.type = "ChoiceField";
            descriptor.config.checkedValue = true;
            descriptor.config.uncheckedValue = null;
            break;
        case "date":
            descriptor.control = "calendar";
            descriptor.type = "DateField";
            descriptor.config.granularity = "DAY";
            break;
        case "datetime":
            descriptor.control = "calendar";
            descriptor.type = "DateTimeField";
            descriptor.config.granularity = "MINUTE";
            break;
        case "time":
            descriptor.control = "spinner";
            descriptor.type = "TimeField";
            descriptor.config.granularity = "MINUTE";
            break;
        case "dropdown":
            descriptor.control = "dropdown";
            descriptor.type = "ChoiceField";
            break;
        case "integer":
            descriptor.control = "spinner";
            descriptor.type = "IntegerField";
            break;
        case "to-one":
            descriptor.control = "section";
            descriptor.type = "Layout";
            break;
        case "to-many":
            descriptor.control = "section";
            descriptor.type = "Layout";
            break;
        default:
            if (/(relative|range|ormissing)$/.test(field.type)) {
                descriptor.control = "row";
                descriptor.type = "Layout";
            } else {
                console.warn(`No "${field.type}" descriptor type is known.`);
                descriptor.control = field.type;
                descriptor.type = field.type;
            }
    }

    if (Array.isArray(field.fields)) {
        // for recursive controls
        descriptor.childItems = field.fields;
    }

    return descriptor;
};



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/search/views/SearchForm.js